"use client";

import { useMemo } from "react";
import { publicProjects, type Project } from "@/data/projects";

// =============================================================================
// USE PROJECT DATA HOOK
// =============================================================================
// Connects orb slug to full project data from projects.ts
// Used by OrbCard and CaseStudyInline to access case study content

export interface UseProjectDataResult {
  project: Project | undefined;
  isProtected: boolean;
  isFound: boolean;
}

/**
 * Get full project data by slug
 * @param slug - The project slug (matches project.id)
 * @returns Project data, protection status, and whether project was found
 */
export function useProjectData(slug: string | undefined): UseProjectDataResult {
  const result = useMemo(() => {
    if (!slug) {
      return { project: undefined, isProtected: false, isFound: false };
    }

    const project = publicProjects.find(p => p.id === slug);

    return {
      project,
      isProtected: project?.isProtected ?? false,
      isFound: !!project,
    };
  }, [slug]);

  return result;
}

/**
 * Get adjacent projects for next/prev navigation
 * @param currentSlug - Current project slug
 * @returns Previous and next project data
 */
export function useAdjacentProjects(currentSlug: string | undefined): {
  prevProject: Project | undefined;
  nextProject: Project | undefined;
} {
  return useMemo(() => {
    if (!currentSlug) {
      return { prevProject: undefined, nextProject: undefined };
    }

    const currentIndex = publicProjects.findIndex(p => p.id === currentSlug);
    if (currentIndex === -1) {
      return { prevProject: undefined, nextProject: undefined };
    }

    // Wrap around for circular navigation
    const prevIndex = (currentIndex - 1 + publicProjects.length) % publicProjects.length;
    const nextIndex = (currentIndex + 1) % publicProjects.length;

    return {
      prevProject: publicProjects[prevIndex],
      nextProject: publicProjects[nextIndex],
    };
  }, [currentSlug]);
}
